/**
 * client/tutorial.ts
 * Responsabilidad: Gestion del tutorial interactivo de 4 pasos.
 *
 * Persistencia: localStorage bajo "alchem_tutorial_state" (JSON).
 * Retrocompatibilidad: si "alchem_tutorial_seen" === "true", se considera completado.
 */

import type { TutorialStepId, TutorialStepState } from "./types";
import { safeGetStorage, safeSetStorage } from "./utils";

const STATE_KEY = "alchem_tutorial_state";
const LEGACY_SEEN_KEY = "alchem_tutorial_seen";

/** Estado por defecto del tutorial */
function createDefaultState(): TutorialStepState {
    return {
        currentStep: "identity",
        foodEatenCount: 0,
        completed: false,
    };
}

/** Carga el estado del tutorial, considerando retrocompatibilidad */
function loadState(): TutorialStepState {
    // Si el jugador rechazo explicitamente el tutorial, marcarlo como completado
    if (safeGetStorage("alchem_wants_tutorial") === "false") {
        return { currentStep: "completed", foodEatenCount: 0, completed: true };
    }
    // Retrocompatibilidad: si el jugador ya vio el tutorial antiguo, esta completado
    if (safeGetStorage(LEGACY_SEEN_KEY) === "true") {
        return { currentStep: "completed", foodEatenCount: 0, completed: true };
    }

    const raw = safeGetStorage(STATE_KEY);
    if (!raw) return createDefaultState();
    try {
        const parsed = JSON.parse(raw) as Partial<TutorialStepState>;
        return {
            currentStep: parsed.currentStep || "identity",
            foodEatenCount: typeof parsed.foodEatenCount === "number" ? parsed.foodEatenCount : 0,
            completed: !!parsed.completed,
        };
    } catch {
        return createDefaultState();
    }
}

export class TutorialManager {
    private state: TutorialStepState;

    constructor() {
        this.state = loadState();
    }

    /** Devuelve el paso actual */
    public getCurrentStep(): TutorialStepId {
        return this.state.currentStep;
    }

    /** Devuelve true si el tutorial esta completado */
    public isCompleted(): boolean {
        return this.state.completed || this.state.currentStep === "completed";
    }

    /** Devuelve cuantas comidas lleva comidas (para el paso 3) */
    public getFoodEatenCount(): number {
        return this.state.foodEatenCount;
    }

    /** Marca el paso 1 (identity) como completado -> avanza a movement */
    public completeIdentity(): void {
        if (this.state.currentStep !== "identity") return;
        this.state.currentStep = "movement";
        this.save();
    }

    /** Avanza de movement a growth */
    public completeMovement(): void {
        if (this.state.currentStep !== "movement") return;
        this.state.currentStep = "growth";
        this.state.foodEatenCount = 0;
        this.save();
    }

    /** Incrementa el contador de comidas. Si llega a 3, avanza a danger */
    public addFoodEaten(): void {
        if (this.state.currentStep !== "growth") return;
        this.state.foodEatenCount += 1;
        if (this.state.foodEatenCount >= 3) {
            this.state.currentStep = "danger";
        }
        this.save();
    }

    /** Marca el tutorial como completado */
    public completeTutorial(): void {
        this.state.currentStep = "completed";
        this.state.completed = true;
        // Retrocompatibilidad: escribir tambien la clave legacy
        safeSetStorage(LEGACY_SEEN_KEY, "true");
        this.save();
    }

    /** Salta el tutorial (usado por el boton "Saltar") */
    public skip(): void {
        this.completeTutorial();
    }

    private save(): void {
        try {
            safeSetStorage(STATE_KEY, JSON.stringify(this.state));
        } catch {
            // Falla silenciosa
        }
    }
}

/** Instancia singleton */
export const tutorialManager = new TutorialManager();
